import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

// Import page components
import Login from '../pages/Auth/Login';
import Register from '../pages/Auth/Register';
import AgeVerification from '../pages/Auth/AgeVerification';
import CompleteProfile from '../pages/Auth/CompleteProfile';
import Forgot from '../pages/Auth/Forgot';
import ResetPassword from '../pages/Auth/ResetPassword';
import Confirm from '../pages/Auth/Confirm';
import OAuthCallback from '../pages/Auth/OAuthCallback'; // ← NUEVO IMPORT
import Landing from '../pages/Landing/Landing';
import PreLaunchLanding from '../pages/Landing/PreLaunchLanding';
import LegalDocumentPage from '../pages/Legal/LegalDocumentPage';
import Settings from '../pages/Settings/Settings';
import Admin from '../pages/Admin/Admin';
import NotFound from '../pages/NotFound/NotFound';
import HeavyDutyMachineries from '../pages/HeavyDutyMachinery/HeavyDutyMachineries';
import MyHeavyDutyMachineries from '../pages/HeavyDutyMachinery/MyHeavyDutyMachineries';
import HeavyDutyMachineryFleet from '../pages/HeavyDutyMachinery/HeavyDutyMachineryFleet';
import MyMachineryAccessories from '../pages/HeavyDutyMachinery/MyMachineryAccessories';
import MyHostInsurancePolicies from '../pages/HeavyDutyMachinery/MyHostInsurancePolicies';
import MyOperators from '../pages/HeavyDutyMachinery/MyOperators';
import MyTransportVehicles from '../pages/HeavyDutyMachinery/MyTransportVehicles';
import MyServices from '../pages/HeavyDutyMachinery/MyServices';
import MyRateCatalog from '../pages/HeavyDutyMachinery/MyRateCatalog';
import RateSheetReview from '../pages/HeavyDutyMachinery/RateSheetReview';
import RequestQuote from '../pages/HeavyDutyMachinery/RequestQuote';
import ServiceRequests from '../pages/HeavyDutyMachinery/ServiceRequests';
import ServiceRequestDetail from '../pages/HeavyDutyMachinery/ServiceRequestDetail';
import ServiceDetail from '../pages/HeavyDutyMachinery/ServiceDetail';
import ServiceForm from '../pages/HeavyDutyMachinery/ServiceForm';
import ServiceAvailability from '../pages/HeavyDutyMachinery/ServiceAvailability';
import ServiceProviderOnboardingPage from '../pages/HeavyDutyMachinery/ServiceProviderOnboardingPage';
import HeavyDutyMachineryForm from '../pages/HeavyDutyMachinery/HeavyDutyMachineryForm';
import HeavyDutyMachineryEditForm from '../pages/HeavyDutyMachinery/HeavyDutyMachineryEditForm';
import HeavyDutyMachineryDetail from '../pages/HeavyDutyMachinery/HeavyDutyMachineryDetail';
import HeavyDutyMachineryBookingPage from '../pages/HeavyDutyMachinery/HeavyDutyMachineryBookingPage';
import HeavyDutyMachineryAvailability from '../pages/HeavyDutyMachinery/HeavyDutyMachineryAvailability';
import MyBookings from '../pages/HeavyDutyMachinery/MyBookings';
import HeavyDutyMachineryFavorites from '../pages/HeavyDutyMachinery/HeavyDutyMachineryFavorites';
import IncomingBookings from '../pages/HeavyDutyMachinery/IncomingBookings';
import Messages from '../pages/HeavyDutyMachinery/Messages';
import Support from '../pages/HeavyDutyMachinery/Support';
import BookingConfirming from '../pages/HeavyDutyMachinery/BookingConfirming';
import CheckInOutFlow from '../pages/HeavyDutyMachinery/CheckInOutFlow';
import BookingTimelinePage from '../pages/HeavyDutyMachinery/BookingTimelinePage';
import BookingDisputePage from '../pages/HeavyDutyMachinery/BookingDisputePage';
import HostDepositPage from '../pages/HeavyDutyMachinery/HostDepositPage';
import HostEarnings from '../pages/HeavyDutyMachinery/HostEarnings';
import HostOnboardingPage from '../pages/HeavyDutyMachinery/HostOnboardingPage';
import HostProfileEditPage from '../pages/HeavyDutyMachinery/HostProfileEditPage';
import ProfileMobile from '../pages/HeavyDutyMachinery/ProfileMobile';
import InfoPage from '../pages/Info/InfoPage';

// Import route wrappers
import ProtectedRoute from '../components/ProtectedRoute';
import PublicRoute from '../components/PublicRoute';
import SiteModeGate, { RegistrationGate } from '../components/SiteModeGate';

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* ======================================== 
          RUTAS PÚBLICAS (sin autenticación)
          ======================================== */}

      {/* Landing Page -- the real one, unless the pre-launch flag is on and
          whoever is asking has no session (see SiteModeGate). */}
      <Route
        path="/"
        element={<SiteModeGate closed={<PreLaunchLanding />} open={<Landing />} />}
      />

      {/* The pre-launch page at its own address, always reachable: it is
          where "todavía no estamos abiertos" sends people, and it is the
          only way to look at the thing while the flag is off. */}
      <Route path="/proximamente" element={<PreLaunchLanding />} />

      {/* Linked from the pre-launch form's consent checkbox, so it has to be
          reachable while everything else is behind the landing. */}
      <Route path="/privacidad" element={<LegalDocumentPage slug="privacidad" />} />

      {/* SiteFooter's placeholder content pages -- public, no-auth. */}
      <Route path="/info/:slug" element={<InfoPage />} />

      {/* Login */}
      <Route 
        path="/login" 
        element={
          <PublicRoute redirectIfAuthenticated={true}>
            <Login />
          </PublicRoute>
        } 
      />

      {/* Register -- behind the RegistrationEnabled flag, which the API
          enforces on its own inside AuthController.Register. This only
          decides whether the form is worth showing. */}
      <Route 
        path="/register" 
        element={
          <PublicRoute redirectIfAuthenticated={true}>
            <RegistrationGate>
              <Register />
            </RegistrationGate>
          </PublicRoute>
        } 
      />

      {/* Forgot Password */}
      <Route 
        path="/forgot" 
        element={
          <PublicRoute>
            <Forgot />
          </PublicRoute>
        } 
      />

      {/* Reset Password */}
      <Route 
        path="/reset-password" 
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        } 
      />

      {/* Confirm Email */}
      <Route 
        path="/confirm" 
        element={
          <PublicRoute>
            <Confirm />
          </PublicRoute>
        } 
      />

      {/* ===== OAUTH CALLBACK - NUEVO ===== */}
      <Route 
        path="/oauth/callback" 
        element={
          <PublicRoute>
            <OAuthCallback />
          </PublicRoute>
        } 
      />

      {/* ======================================== 
          RUTAS PROTEGIDAS (requieren autenticación)
          ======================================== */}

      {/* Age verification — post-registration step for accounts on
          applications that require it (see Application.RequireAgeVerification) */}
      <Route
        path="/verify-age"
        element={
          <ProtectedRoute>
            <AgeVerification />
          </ProtectedRoute>
        }
      />

      {/* Registration's step 2 for this app -- see UserProfileProvider/ProtectedRoute */}
      <Route
        path="/complete-profile"
        element={
          <ProtectedRoute>
            <CompleteProfile />
          </ProtectedRoute>
        }
      />

      {/* Old post-login landing spot -- Landing.tsx is now the same page for
          everyone (signed in or not, see AppRoutes.tsx's "/" route and
          Landing.tsx's own auth branch), so this is just an alias kept for
          every hardcoded navigate('/dashboard') call already in the app
          (AuthProvider.login()'s default target, AppLogo, ProtectedRoute's
          requireAdmin fallback...). */}
      <Route path="/dashboard" element={<Navigate to="/" replace />} />

      {/* Mi cuenta -- retired as its own page; folded into Configuración's
          "Información personal" section (see Settings.tsx). Kept as an alias
          so every existing navigate('/profile') call still lands somewhere,
          same convention as /dashboard -> /. */}
      <Route path="/profile" element={<Navigate to="/settings?section=personal" replace />} />

      {/* Settings */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Admin panel -- Admin or Moderator only, enforced both here (frontend)
          and server-side by every /api/heavy-duty-machinery/admin/* endpoint (see
          AdminController). */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdminOrModerator>
            <Admin />
          </ProtectedRoute>
        }
      />

      {/* HeavyDutyMachinery spaces */}
      {/* Public, no-auth search/browse -- anyone should be able to see the
          listings before creating an account. Login is only required for
          actions that need identity (booking, host-only management), gated
          inline where those live. */}
      <Route path="/heavy-duty-machinery" element={<HeavyDutyMachineries />} />
      <Route
        path="/heavy-duty-machinery/mine"
        element={
          <ProtectedRoute>
            <MyHeavyDutyMachineries />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/fleet"
        element={
          <ProtectedRoute>
            <HeavyDutyMachineryFleet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/accessories"
        element={
          <ProtectedRoute>
            <MyMachineryAccessories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/insurance-policies"
        element={
          <ProtectedRoute>
            <MyHostInsurancePolicies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/operators"
        element={
          <ProtectedRoute>
            <MyOperators />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/transport-vehicles"
        element={
          <ProtectedRoute>
            <MyTransportVehicles />
          </ProtectedRoute>
        }
      />
      {/* Fase 13: Services -- a provider's own catalog, independent of
          HostProfile (see ServiceProviderProfile's own doc comment: a user
          can be a ServiceProvider without ever being a Host), so this stays
          reachable even for a caller with no HostProfile at all, unlike
          every host-only route above. New/edit both go through the same
          wizard component (see ServiceForm's own doc comment for why, unlike
          Machinery's separate create-wizard/edit-form split). */}
      {/* Public, no-auth detail + checkout -- same convention as
          /heavy-duty-machinery/:id for a Machinery space. Booking itself is
          gated on isAuthenticated inline (see ServiceDetail's own doc
          comment), not by ProtectedRoute here. */}
      <Route path="/heavy-duty-machinery/services/:id" element={<ServiceDetail />} />
      <Route
        path="/heavy-duty-machinery/services"
        element={
          <ProtectedRoute>
            <MyServices />
          </ProtectedRoute>
        }
      />

      {/* "Mi tarifa" -- what this provider charges, and the price lists it
          came from. The review screen is its own route rather than a modal:
          it is a long, careful pass over hundreds of rows, and it deserves
          a back button and a URL somebody can return to. */}
      <Route
        path="/heavy-duty-machinery/my-rates"
        element={
          <ProtectedRoute>
            <MyRateCatalog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/my-rates/:id/review"
        element={
          <ProtectedRoute>
            <RateSheetReview />
          </ProtectedRoute>
        }
      />
      {/* Pedir presupuesto: contar el trabajo, sin elegir fecha. Las dos
          listas comparten pantalla porque son la misma lista vista desde los
          dos lados -- ver ServiceRequests. */}
      <Route
        path="/heavy-duty-machinery/services/:id/request-quote"
        element={
          <ProtectedRoute>
            <RequestQuote />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/service-requests"
        element={
          <ProtectedRoute>
            <ServiceRequests mode="mine" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/service-requests/incoming"
        element={
          <ProtectedRoute>
            <ServiceRequests mode="incoming" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/service-requests/:id"
        element={
          <ProtectedRoute>
            <ServiceRequestDetail />
          </ProtectedRoute>
        }
      />
      {/* Self-registration for the independent ServiceProviderProfile
          modality -- see ServiceProviderOnboardingPage's own doc comment for
          why this stays reachable (and resumable) even after a profile
          already exists, unlike /host/onboarding. */}
      <Route
        path="/service-provider/onboarding"
        element={
          <ProtectedRoute>
            <ServiceProviderOnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/services/new"
        element={
          <ProtectedRoute>
            <ServiceForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/services/:id/edit"
        element={
          <ProtectedRoute>
            <ServiceForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/services/:id/availability"
        element={
          <ProtectedRoute>
            <ServiceAvailability />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/bookings/mine"
        element={
          <ProtectedRoute>
            <MyBookings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/favorites"
        element={
          <ProtectedRoute>
            <HeavyDutyMachineryFavorites />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/bookings/incoming"
        element={
          <ProtectedRoute>
            <IncomingBookings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/messages"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/support"
        element={
          <ProtectedRoute>
            <Support />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/booking-confirming"
        element={
          <ProtectedRoute>
            <BookingConfirming />
          </ProtectedRoute>
        }
      />
      {/* Per-booking progress view, same screen for guest and host. The
          dynamic :id never shadows /bookings/incoming above -- React Router
          ranks a static segment ahead of a dynamic one regardless of order. */}
      <Route
        path="/heavy-duty-machinery/bookings/:id"
        element={
          <ProtectedRoute>
            <BookingTimelinePage />
          </ProtectedRoute>
        }
      />
      {/* One booking's claim, for either party -- doubles as the place to
          file one when the booking has none yet. */}
      <Route
        path="/heavy-duty-machinery/bookings/:id/dispute"
        element={
          <ProtectedRoute>
            <BookingDisputePage />
          </ProtectedRoute>
        }
      />
      {/* A host's own guarantee: what it is, what it lets them carry, and
          how to add to it. Linked from the deposit notifications and from
          the "top up to accept this" prompt on a quote. */}
      <Route
        path="/heavy-duty-machinery/host/deposit"
        element={
          <ProtectedRoute>
            <HostDepositPage />
          </ProtectedRoute>
        }
      />
      {/* A host's own money: how much, when, from which machine, and where
          it is right now. The same figures AdminFinanceController has always
          had, on the side of the mirror the person earning them stands on. */}
      <Route
        path="/heavy-duty-machinery/host/earnings"
        element={
          <ProtectedRoute>
            <HostEarnings />
          </ProtectedRoute>
        }
      />
      {/* Fase 6: guided check-in/check-out capture -- reportType is CheckIn|CheckOut. */}
      <Route
        path="/heavy-duty-machinery/bookings/:id/check-in-out/:reportType"
        element={
          <ProtectedRoute>
            <CheckInOutFlow />
          </ProtectedRoute>
        }
      />
      {/* Onboarding wizard -- always a full page, never a modal. Reached via
          the header's or footer's "List your space" link when the user has
          no HostProfile yet (see AppHeader/SiteFooter). */}
      <Route
        path="/host/onboarding"
        element={
          <ProtectedRoute>
            <HostOnboardingPage />
          </ProtectedRoute>
        }
      />
      {/* Perfil de anfitrión -- retired as its own page; folded into
          Configuración's "Perfil de anfitrión" section (see Settings.tsx).
          Kept as an alias, same convention as /dashboard and /profile above,
          so HostProfileEditPage's back/cancel targets still land somewhere. */}
      <Route path="/host/profile" element={<Navigate to="/settings?section=hostProfile" replace />} />
      {/* Mobile bottom nav's "Perfil" tab -- see MobileBottomNav/ProfileMobile. */}
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <ProfileMobile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/profile/edit/:section"
        element={
          <ProtectedRoute>
            <HostProfileEditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/new"
        element={
          <ProtectedRoute>
            <HeavyDutyMachineryForm />
          </ProtectedRoute>
        }
      />
      {/* Editing an already-published space uses a dedicated collapsible-
          sections form (HeavyDutyMachineryEditForm), not the paged wizard above --
          see that page's own doc comment for why. */}
      <Route
        path="/heavy-duty-machinery/:id/edit"
        element={
          <ProtectedRoute>
            <HeavyDutyMachineryEditForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/heavy-duty-machinery/:id/availability"
        element={
          <ProtectedRoute>
            <HeavyDutyMachineryAvailability />
          </ProtectedRoute>
        }
      />
      {/* Multi-step checkout -- pulled out of the detail page onto its own
          route (see HeavyDutyMachineryBookingPage's own doc comment). Same
          public, no-auth convention as /heavy-duty-machinery/:id: booking
          itself is still gated inline inside HeavyDutyMachineryBookingWidget. */}
      <Route path="/heavy-duty-machinery/:id/book" element={<HeavyDutyMachineryBookingPage />} />
      {/* Same as /heavy-duty-machinery above -- public, no-auth. Booking is gated
          inline inside HeavyDutyMachineryBookingWidget instead. */}
      <Route path="/heavy-duty-machinery/:id" element={<HeavyDutyMachineryDetail />} />

      {/* Not Found */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;